<template>
  <div class="lc-oscillation-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$T = 2π√(LC)$</div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <svg width="100%" height="240" viewBox="0 0 400 240" preserveAspectRatio="xMidYMid meet">
          <defs>
             <filter id="glow-l"><feGaussianBlur stdDeviation="3" result="blur"/><feComposite in="SourceGraphic" in2="blur" operator="over"/></filter>
             <filter id="glow-c"><feGaussianBlur stdDeviation="6" result="blur"/><feComposite in="SourceGraphic" in2="blur" operator="over"/></filter>
          </defs>

          <!-- 电路连线 -->
          <path d="M 60,120 V 60 H 140 M 260,60 H 340 V 120 M 60,180 V 220 H 340 V 180" 
                fill="none" stroke="#1e293b" stroke-width="3" stroke-linecap="round" />

          <!-- 电感线圈 (L) -->
          <g transform="translate(140, 60)">
             <path d="M 0,0 Q 15,-25 30,0 Q 45,-25 60,0 Q 75,-25 90,0 Q 105,-25 120,0" 
                   fill="none" stroke="#38bdf8" stroke-width="4" stroke-linecap="round" 
                   :opacity="0.3 + Math.abs(current)/2 * 0.7" filter="url(#glow-l)" />
             <!-- 动态磁感线 -->
             <g v-if="Math.abs(current) > 0.1">
                <ellipse v-for="n in 3" :key="'m'+n" 
                        cx="60" cy="0" :rx="20 + n*15" :ry="10 + n*8" 
                        fill="none" stroke="#0ea5e9" :stroke-width="0.5" 
                        :opacity="Math.abs(current)/4 * (1/n)" />
             </g>
             <text x="60" y="-35" text-anchor="middle" fill="#0ea5e9" font-size="10" font-weight="900">电感 L (磁场能)</text>
          </g>

          <!-- 电容器 (C) -->
          <g transform="translate(180, 160)">
             <!-- 极板 -->
             <line x1="-30" y1="-20" x2="30" y2="-20" stroke="#f43f5e" stroke-width="4" />
             <line x1="-30" y1="20" x2="30" y2="20" stroke="#fbbf24" stroke-width="4" />
             <!-- 电场线 (极板间) -->
             <line v-for="n in 5" :key="'e'+n" 
                   :x1="-20 + n*8" y1="-15" :x2="-20 + n*8" y2="15" 
                   stroke="#f43f5e" :opacity="Math.abs(charge)/10" stroke-width="1" />
             
             <!-- 极板电荷表示 -->
             <g v-if="Math.abs(charge) > 0.5">
                <text v-for="i in 3" :key="'p'+i" :x="-20 + i*15" y="-28" fill="#f43f5e" font-size="12" font-weight="bold">{{ charge > 0 ? '+' : '-' }}</text>
                <text v-for="i in 3" :key="'n'+i" :x="-20 + i*15" y="32" fill="#fbbf24" font-size="12" font-weight="bold">{{ charge > 0 ? '-' : '+' }}</text>
             </g>
             <text x="0" y="55" text-anchor="middle" fill="#f43f5e" font-size="10" font-weight="900">电容 C (电场能)</text>
          </g>

          <!-- 能量实时转化条 -->
          <g transform="translate(310, 20)">
             <rect width="80" height="60" fill="rgba(15, 23, 42, 0.8)" rx="6" stroke="#1e293b" />
             <rect x="10" y="45" width="25" :height="-we*0.4" fill="#f43f5e" rx="2" />
             <rect x="45" y="45" width="25" :height="-wm*0.4" fill="#0ea5e9" rx="2" />
             <text x="22" y="56" text-anchor="middle" fill="#94a3b8" font-size="7">We</text>
             <text x="57" y="56" text-anchor="middle" fill="#94a3b8" font-size="7">Wm</text>
          </g>
        </svg>

        <div class="hud-panel">
           <div class="hud-item orange">
              <label>当前主回路电流</label>
              <span class="val">{{ current.toFixed(2) }} <small>A</small></span>
           </div>
        </div>
      </div>

      <div class="compact-controls">
        <div class="control-card">
           <div class="flex flex-row items-center gap-6">
              <div class="flex-1">
                 <label class="s-label">振荡周期调节 (LC 乘积)</label>
                 <el-slider v-model="lcFactor" :min="10" :max="100" size="small"></el-slider>
              </div>
              <el-button type="danger" size="mini" @click="resetOscillation">初始充电</el-button>
           </div>
        </div>
        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-lightning"></i>
           <p><strong>能量守恒揭示：</strong>LC 电路是电荷的“秋千”。当电荷离开电容器板时，流经电感的电流逐渐增大，将电场能转化为<strong>磁场能</strong>；当电流达到最大后，磁场能开始向反向的电场能回流。这就是无线电广播发射信号的物理心脏。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LCOscillationViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      lcFactor: 50,
      t: 0,
      charge: 10,
      current: 0,
      running: true
    }
  },
  watch: {
    knowledgePoint: {
       handler() { this.detectMode(); },
       immediate: true
    }
  },
  computed: {
    // Energies for display
    we() { return 0.5 * (this.charge * this.charge); },
    wm() { return 0.5 * (this.current * this.current) * (this.lcFactor/10); }
  },
  methods: {
    detectMode() {
      const kp = (this.knowledgePoint || '').toString();
      if (/周期|频率/.test(kp)) this.lcFactor = 80;
      else if (/能量/.test(kp)) this.lcFactor = 30;
    },
    resetOscillation() {
       this.charge = 10;
       this.current = 0;
       this.t = 0;
    },
    animate() {
       if (this.running) {
          const dt = 0.016;
          // Simple harmonic motion for LC: d2q/dt2 + (1/LC)q = 0
          const w = 1 / Math.sqrt(this.lcFactor / 20);
          this.t += dt;
          
          // Analytical solution for visual stability
          this.charge = 10 * Math.cos(w * this.t * 5);
          this.current = -10 * w * Math.sin(w * this.t * 5);
       }
       this.animFrame = requestAnimationFrame(this.animate);
    }
  },
  mounted() { this.animate(); },
  beforeDestroy() { cancelAnimationFrame(this.animFrame); }
}
</script>

<style scoped lang="scss">
.lc-oscillation-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 2px 4px; .brief { font-size: 13.5px; color: #1e293b; font-weight: 500; } .formula-chip { background: #fef2f2; color: #991b1b; border: 1px solid #fecaca; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { min-height: 240px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; display: flex; align-items: center; justify-content: center; }
.hud-panel { position: absolute; top: 12px; left: 12px; z-index: 10; .hud-item { background: rgba(15, 23, 42, 0.85); border-radius: 8px; border: 1px solid #334155; padding: 4px 12px; border-left: 3px solid #f97316; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; } .val { font-size: 14px; color: #f8fafc; font-weight: 800; font-family: monospace; } } }
.compact-controls { display: flex; flex-direction: column; gap: 6px; }
.control-card { background: #ffffff; padding: 10px 14px; border-radius: 12px; border: 1px solid #e2e8f0; }
.s-label { font-size: 11px; color: #64748b; font-weight: 800; margin-bottom: 2px; display: block; }
.flex { display: flex; } .items-center { align-items: center; } .gap-6 { gap: 24px; } .flex-1 { flex: 1; }
.discovery-card-compact { background: #f0fdf4; padding: 10px 14px; border-radius: 10px; border: 1px solid #dcfce7; display: flex; align-items: center; gap: 10px; i { color: #10b981; font-size: 1.2rem; } p { flex: 1; margin: 0; font-size: 12px; color: #166534; line-height: 1.5; strong { color: #14532d; } } }
</style>
